import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import {
  History, MessageSquare, ThumbsUp, UserPlus, Mail, FileUp, ImageIcon, Trash2, Camera, Check, Edit, Star, AlertCircle,
} from "lucide-react";
import { fmtRelative } from "@/lib/format";

const ICON = {
  "comment_added": MessageSquare,
  "comment.resolved": Check,
  "comment.reopened": Edit,
  "reviewer.invited": UserPlus,
  "reviewer.invite_emailed": Mail,
  "reviewer.first_visit": History,
  "version.uploaded": FileUp,
  "version.screenshot": Camera,
  "review_item.approved": ThumbsUp,
  "review_item.rated": Star,
  "review_item.deleted": Trash2,
  "project.deleted": Trash2,
};

/**
 * ActivityLogDialog — per-item timeline of every change/share/comment/invite/email/approval.
 * Lives next to Share + Reviewers buttons on a review item.
 */
export default function ActivityLogDialog({ itemId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/review-items/${itemId}/activity`);
      setRows(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open, itemId]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-8" data-testid="activity-log-open-btn">
          <History className="w-3.5 h-3.5 mr-1.5" /> Activity
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Activity log</DialogTitle>
          <DialogDescription>
            Every change to this review item — comments, invites sent, emails delivered, versions, ratings, approvals.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[70vh] overflow-auto -mx-6 px-6">
          {loading && <div className="text-sm text-muted-foreground py-8 text-center">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center" data-testid="activity-log-empty">
              No activity recorded yet.
            </div>
          )}
          <ol className="space-y-3">
            {rows.map((r) => {
              const Icon = ICON[r.action] || AlertCircle;
              const meta = r.metadata || {};
              return (
                <li key={r.id || r.created_at} className="flex items-start gap-3 border-l-2 border-border pl-3 py-1" data-testid={`activity-row-${r.action}`}>
                  <Icon className="w-4 h-4 mt-0.5 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono">
                      <span className="font-medium">{r.actor_name || "system"}</span>
                      <span className="text-muted-foreground mx-1">·</span>
                      <span className="font-mono">{r.action.replace(/[._]/g, " ")}</span>
                    </div>
                    {meta.reviewer_email && (
                      <div className="text-xs text-muted-foreground">
                        Reviewer: <span className="font-mono">{meta.reviewer_name}</span> &lt;{meta.reviewer_email}&gt;
                      </div>
                    )}
                    {meta.url && (
                      <div className="text-[10px] text-muted-foreground font-mono truncate">{meta.url}</div>
                    )}
                    {meta.version_number && (
                      <div className="text-[10px] text-muted-foreground">v{meta.version_number}</div>
                    )}
                    {meta.body_preview && (
                      <div className="text-xs text-muted-foreground italic mt-0.5 truncate">&ldquo;{meta.body_preview}&rdquo;</div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">{fmtRelative(r.created_at)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      </DialogContent>
    </Dialog>
  );
}
